import { AxiosError } from "axios";

import { apiClient } from "./client";

/**
 * Client for the retention download endpoints (`/api/retention/events/...`).
 *
 * These are the endpoints the retention emails point at. Three things about them shape
 * this module:
 *
 *  1. **Bytes are proxied, never redirected.** The backend deliberately mints no signed
 *     or public storage URL, so entitlement is re-checked on every request and a link
 *     stops working the moment the retention deadline passes or the deletion is restored.
 *     A plain `<a href>` would therefore 401, because the browser sends no bearer token —
 *     every download has to go through `apiClient` as a blob.
 *  2. **Denials are deliberately indistinguishable.** An unknown event, another agency's
 *     event, an expired window, and a missing permission all answer the same 404-shaped
 *     response so nothing leaks through probing. The UI must not try to infer which one
 *     it hit; it says "no longer available" and offers the status page.
 *  3. **The status projection is the source of truth for what is downloadable.**
 *     `downloads_available` already folds in the deadline and the restoration, so the
 *     page gates on that single flag rather than recomputing the rule in the browser.
 */

/** One retained media object of a Deletion_Event. */
export interface RetentionMediaEntry {
  media_id: string;
  label: string;
  media_kind: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  availability: string | null;
  ownership: string | null;
  /** Null when the object is already absent from storage — nothing to fetch. */
  download_path: string | null;
}

/** The current export workbook, when one has been generated. */
export interface RetentionArtifact {
  artifact_id: string;
  file_name: string | null;
  size_bytes: number | null;
  generated_at: string | null;
  download_path: string;
}

/** The status projection of one Deletion_Event, as its Responsible_User sees it. */
export interface RetentionEventStatus {
  event_id: string;
  root_type: string;
  root_id: string;
  root_label: string | null;
  deletion_epoch: number;
  deleted_at: string | null;
  retention_start: string | null;
  retention_deadline: string | null;
  remaining_days: number | null;
  restored_at: string | null;
  /** The single fact that governs whether any download on this page will work. */
  downloads_available: boolean;
  export_state: string;
  delivery_state: string;
  cleanup_state: string;
  inventory_complete: boolean;
  artifact: RetentionArtifact | null;
  media: RetentionMediaEntry[];
}

/** Why the page cannot show anything, in the only granularity the API offers. */
export type RetentionStatusErrorKind =
  | "unavailable"
  | "unauthenticated"
  | "failed";

export class RetentionStatusError extends Error {
  constructor(
    message: string,
    public readonly kind: RetentionStatusErrorKind,
  ) {
    super(message);
    this.name = "RetentionStatusError";
  }
}

/**
 * Fetch the status of one Deletion_Event.
 *
 * A 403/404 is reported as `unavailable` rather than as a failure: it is the expected
 * answer for a link whose window has closed, and the page renders it as information, not
 * as an error.
 */
export async function fetchRetentionEventStatus(
  eventId: string,
): Promise<RetentionEventStatus> {
  try {
    const response = await apiClient.get(`/retention/events/${eventId}`);

    return response.data?.data as RetentionEventStatus;
  } catch (error) {
    throw toStatusError(
      error,
      "This deletion is no longer available to download.",
    );
  }
}

/** Fetch the export workbook and hand it to the browser. */
export async function downloadRetentionArtifact(
  eventId: string,
  artifactId: string,
  fileName: string,
): Promise<void> {
  await streamToBrowser(
    `/retention/events/${eventId}/artifacts/${artifactId}/download`,
    fileName,
    "The export is no longer available to download.",
  );
}

/** Fetch one retained media object and hand it to the browser. */
export async function downloadRetentionMedia(
  eventId: string,
  mediaId: string,
  fileName: string,
): Promise<void> {
  await streamToBrowser(
    `/retention/events/${eventId}/media/${mediaId}/download`,
    fileName,
    "That file is no longer available to download.",
  );
}

/**
 * Pull a proxied stream down as a blob and trigger a save.
 *
 * The server's `Content-Type` is preserved on the blob so the browser treats a video as a
 * video and a workbook as a workbook, and the object URL is revoked immediately after the
 * click so a large media file is not held in memory for the life of the page.
 */
async function streamToBrowser(
  path: string,
  fileName: string,
  unavailableMessage: string,
): Promise<void> {
  let objectUrl: string | null = null;

  try {
    const response = await apiClient.get(path, { responseType: "blob" });

    const contentType =
      (response.headers?.["content-type"] as string | undefined) ??
      "application/octet-stream";

    objectUrl = window.URL.createObjectURL(
      new Blob([response.data], { type: contentType }),
    );

    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    throw toStatusError(error, unavailableMessage);
  } finally {
    if (objectUrl) {
      window.URL.revokeObjectURL(objectUrl);
    }
  }
}

/**
 * Map a transport failure onto the three cases the page can act on.
 *
 * A blob-typed error body arrives as a Blob rather than JSON, so the server's message is
 * unreadable without another async read. It is not worth reading: the message is
 * deliberately uninformative, and the status code is the whole signal.
 */
function toStatusError(
  error: unknown,
  unavailableMessage: string,
): RetentionStatusError {
  const status = (error as AxiosError)?.response?.status;

  if (status === 401) {
    return new RetentionStatusError(
      "Please sign in to open this download.",
      "unauthenticated",
    );
  }

  if (status === 403 || status === 404) {
    return new RetentionStatusError(unavailableMessage, "unavailable");
  }

  return new RetentionStatusError(
    "Something went wrong loading this download.",
    "failed",
  );
}
